/**
 * Year-1 Impact Assembly Module
 *
 * Collects pre-computed values for Year-1 savings illustration.
 * NO RECALCULATION - only assembles data already computed by the engine.
 *
 * CRITICAL: This module never invents values. If data is missing:
 * - Use fallback
 * - Label the assumption
 * - Lower confidence
 * - Never return 0 silently
 */

import type { Year1Impact, Year1Comparison, SystemRecOutput, RecommendationOption, LossesBreakdown, SystemRecInput } from './types';
import type { BaselineResult } from './baseline';
import { CARBON_INTENSITY } from './carbonEmissions';
import {
  deriveSedbukBand,
  formatSeasonalPct,
  formatSedbukDisplay,
  applyBoilerTypeModifier,
  getCylinderStandingLoss,
  BOILER_TYPE_MODIFIERS,
  CYLINDER_STANDING_LOSS,
} from './efficiency';
import { getEffectiveSPF } from './heatPumpConfidence';
import { getEffectiveSPFRange } from './flowTempEstimation';
import { estimateDemand } from './demand';
import { resolveAssumptionsSafe, type ResolvedAssumption, type EnergyPricesValues } from './assumptions';

// ==================== TYPES ====================

interface PerformanceMetadata {
  value: number;
  label: string;
  source: "sedbuk" | "epc" | "heat_loss_band" | "assumed";
  confidence: "high" | "medium" | "low";
  // Optional SPF range for heat pumps (when using flow-temp-based estimation)
  spfRange?: {
    min: number;
    max: number;
    flowBand?: string;
  };
}

interface ScenarioData {
  annualHeatDemand_kWh: number;
  systemEfficiency: number;
  fuelType: 'gas' | 'electricity';
  fuelCostPPerKwh: number;
  performance: PerformanceMetadata;
  boilerType: string;      // e.g., 'combi', 'system', 'regular', 'air_source_hp'
  cylinderType: string;    // e.g., 'on_demand', 'unvented', 'open_vented', 'mixergy_unvented'
}

// ==================== ENERGY PRICES ====================

// Cached resolved prices (resolved once per module load, or per report date)
let _cachedPrices: ResolvedAssumption<EnergyPricesValues> | null = null;
let _cachedPricesAsOf: string | null = null;

/**
 * Get energy prices from the assumptions registry.
 * Caches the result for the same asOf date to avoid repeated lookups.
 */
export function getEnergyPrices(asOf?: Date): ResolvedAssumption<EnergyPricesValues> {
  const asOfKey = asOf ? asOf.toISOString().slice(0, 10) : 'current';

  if (_cachedPrices && _cachedPricesAsOf === asOfKey) {
    return _cachedPrices;
  }

  _cachedPrices = resolveAssumptionsSafe('energy_prices', { asOf });
  _cachedPricesAsOf = asOfKey;
  return _cachedPrices;
}

/**
 * Get tariff in p/kWh for a fuel type.
 * Uses the assumptions registry with fallback to inline constants.
 */
function getTariffPPerKwh(fuelType: 'gas' | 'electricity', asOf?: Date): number {
  const prices = getEnergyPrices(asOf);
  return fuelType === 'gas'
    ? prices.values.gas_unit_rate_p_per_kwh
    : prices.values.electricity_unit_rate_p_per_kwh;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Estimate space heat vs DHW split
 * Typical UK: 80% space heat, 20% DHW
 */
function estimateHeatSplit(totalHeatKwh: number): { spaceHeat: number; dhw: number } {
  const spaceHeatRatio = 0.80;
  const dhwRatio = 0.20;

  return {
    spaceHeat: Math.round(totalHeatKwh * spaceHeatRatio),
    dhw: Math.round(totalHeatKwh * dhwRatio),
  };
}

/**
 * Get fuel type for a system
 */
function getFuelTypeForSystem(boiler: string): 'gas' | 'electricity' {
  if (boiler === 'air_source_hp' || boiler === 'ground_source_hp') {
    return 'electricity';
  }
  // Default to gas for all boiler types
  return 'gas';
}

/**
 * Get system efficiency from option or baseline
 * Returns efficiency (0-1) or SCOP for heat pumps
 *
 * Uses documented real-world efficiencies from Data/Performance.csv
 */
function getSystemEfficiency(boiler: string, lcoo?: any): number {
  // For heat pumps, use SCOP if available
  if (boiler === 'air_source_hp') {
    return 3.5; // Conservative SCOP estimate
  }
  if (boiler === 'ground_source_hp') {
    return 4.0; // GSHP typically higher efficiency
  }

  // For gas boilers, use ERP-rated efficiency: 93% (not 94%)
  // Source: Data/Performance.csv, real-world field tests
  return 0.93;
}

/**
 * Get performance metadata for baseline (existing system)
 * Uses canonical efficiency helpers for consistent band derivation and formatting
 */
function getBaselinePerformance(baseline: BaselineResult): PerformanceMetadata {
  const efficiency = baseline.initialEfficiency;
  const source = baseline.efficiencySource || 'assumed';

  // Determine confidence based on source
  let confidence: "high" | "medium" | "low" = "low";
  let sourceType: "sedbuk" | "epc" | "heat_loss_band" | "assumed" = "assumed";

  if (source.includes('SEDBUK') || source.includes('GC')) {
    confidence = "high";
    sourceType = "sedbuk";
  } else if (source.includes('EPC')) {
    confidence = "medium";
    sourceType = "epc";
  } else if (source.includes('heat loss') || source.includes('age')) {
    confidence = "low";
    sourceType = "heat_loss_band";
  }

  // Format label using canonical helpers
  const pct = formatSeasonalPct(efficiency);
  let label = `${pct}% efficiency`;

  if (sourceType === "sedbuk") {
    // Use canonical SEDBUK display format: "SEDBUK band X (Y%)"
    label = formatSedbukDisplay(efficiency);
  }

  return {
    value: efficiency,
    label,
    source: sourceType,
    confidence,
  };
}

/**
 * Get performance metadata for replacement option
 * 
 * For heat pumps, uses flow-temp-based SPF range estimation when input is available,
 * otherwise falls back to assumed SPF value.
 */
function getOptionPerformance(option: RecommendationOption, input?: SystemRecInput, heatLossKw?: number): PerformanceMetadata {
  const boiler = option.boiler;

  // Heat pumps use SPF range estimation based on flow temp requirements
  if (boiler === 'air_source_hp' || boiler === 'ground_source_hp') {
    const assumedSPF = boiler === 'air_source_hp' ? 3.5 : 4.0;
    
    if (input) {
      // Use new flow-temp-based SPF range estimation
      const spfRange = getEffectiveSPFRange(input, heatLossKw);
      
      // Use mid-point of range as the "value" for calculations
      const midPoint = (spfRange.min + spfRange.max) / 2;
      
      return {
        value: midPoint,
        label: `SPF ${spfRange.min.toFixed(1)}-${spfRange.max.toFixed(1)} (${spfRange.confidence} confidence)`,
        source: 'assumed',
        confidence: spfRange.confidence,
        spfRange: {
          min: spfRange.min,
          max: spfRange.max,
          flowBand: spfRange.flowBand,
        },
      };
    }
    
    // Fallback: no input available, use simple assumption
    return {
      value: assumedSPF,
      label: `SPF ${assumedSPF.toFixed(1)}`,
      source: 'assumed',
      confidence: 'medium',
    };
  }

  // Gas boilers - assume modern condensing efficiency
  return {
    value: 0.90,
    label: '90% efficiency',
    source: 'assumed',
    confidence: 'medium',
  };
}

// ==================== CORE BUILD FUNCTION ====================

/**
 * Build Year-1 impact data from scenario information
 *
 * Uses documented real-world efficiencies from Data/Performance.csv:
 * - Combi DHW: 78% (field values 75-82%, line 22)
 * - Unvented cylinder DHW: 68% (65% base + 3% modulation benefit, line 24)
 * - Open-vented cylinder DHW: 55% (low-use houses hit by standing loss)
 * - Mixergy: >93% (prevents hunting/short cycling, reduces standing loss)
 *
 * Applies boiler type modifiers (system vs regular) and cylinder standing losses
 * with proper evidence tracking.
 *
 * @param scenario - Scenario data (baseline or option)
 * @returns Year1Impact object with all Year-1 metrics
 */
export function buildYear1Impact(scenario: ScenarioData): Year1Impact {
  const totalHeatKwh = scenario.annualHeatDemand_kWh;
  let baseEfficiency = scenario.systemEfficiency; // 93% ERP for gas boilers
  const fuelType = scenario.fuelType;
  const fallbackFuelCost = getTariffPPerKwh(fuelType);
  const fuelCostPPerKwh = Number.isFinite(scenario.fuelCostPPerKwh) && scenario.fuelCostPPerKwh > 0
    ? scenario.fuelCostPPerKwh
    : fallbackFuelCost;

  if (!Number.isFinite(baseEfficiency) || baseEfficiency <= 0) {
    baseEfficiency = 0.75;
  }
  const isMixergy = scenario.cylinderType === 'mixergy_unvented' || scenario.cylinderType === 'mixergy_open';
  const isCombi = scenario.boilerType === 'combi' || scenario.cylinderType === 'on_demand';
  const isUnvented = scenario.cylinderType === 'unvented' || scenario.cylinderType === 'mixergy_unvented';
  const isOpenVented = scenario.cylinderType === 'open_vented' || scenario.cylinderType === 'mixergy_open';
  const isSystem = scenario.boilerType === 'system';
  const isRegular = scenario.boilerType === 'regular';

  // Apply boiler type efficiency modifier for gas boilers
  // System boilers: sealed system, minimal distribution losses
  // Regular boilers: typically open-vented, or sealed with unvented cylinder
  //   In both cases, additional pipework/distribution losses (~3%)
  if (fuelType === 'gas' && (isSystem || isRegular)) {
    const { adjustedEfficiency, modifier } = applyBoilerTypeModifier(baseEfficiency, scenario.boilerType);
    if (modifier) {
      baseEfficiency = adjustedEfficiency;
      // Evidence: modifier applied and reason recorded
      // (In a full implementation, this would be added to evidence.appliedInputs)
    }
  }

  // Estimate space heat vs DHW split
  const { spaceHeat, dhw } = estimateHeatSplit(totalHeatKwh);

  // Apply real-world DHW efficiency penalties (Data/Performance.csv lines 22-24)
  // These account for standing losses, modulation losses, and system characteristics
  let dhwEfficiency = baseEfficiency;

  if (fuelType === 'gas') {
    if (isMixergy) {
      // Mixergy achieves >93% by preventing hunting/short cycling
      // Also reduces standing loss and unused heated water
      dhwEfficiency = 0.95; // >93% documented benefit
    } else if (isCombi) {
      // Combi real-world DHW: 78% (vs 93% ERP rating)
      // Field values 75-82%, short draws cause modulation losses
      dhwEfficiency = 0.78;
    } else if (isUnvented) {
      // Unvented real-world: 68% (65% base + 3% modulation benefit from longer burns)
      // Better insulation than open-vented, better temperature control
      dhwEfficiency = 0.68;
    } else if (isOpenVented) {
      // Open-vented: 55% (low-use houses hit by standing loss)
      // Older insulation, loft location, less controlled temperature
      dhwEfficiency = 0.55;
    } else {
      // Default for unknown cylinder types with gas boiler
      dhwEfficiency = 0.60;
    }
  }

  // Get cylinder standing loss based on type
  const cylinderLoss = getCylinderStandingLoss(scenario.cylinderType);
  // Evidence: cylinderLoss.reason describes why this value was used

  // Calculate fuel separately for space heat and DHW
  const spaceHeatFuel = spaceHeat / baseEfficiency;
  const dhwFuel = dhw / dhwEfficiency;
  const fuelKwh = spaceHeatFuel + dhwFuel;

  // Build fuel breakdown
  const fuelBreakdown: Year1Impact['fuelKwh'] = {};
  if (fuelType === 'gas') {
    fuelBreakdown.gas = Math.round(fuelKwh);
  } else {
    fuelBreakdown.electricity = Math.round(fuelKwh);
  }

  // Calculate cost (£/year)
  const costGbp = (fuelKwh * fuelCostPPerKwh) / 100; // Convert pence to pounds

  // Calculate carbon (kgCO2/year)
  const carbonIntensity = fuelType === 'gas'
    ? CARBON_INTENSITY.gas
    : CARBON_INTENSITY.electricity[2024]; // Use current year
  const carbonKg = fuelKwh * carbonIntensity;

  // ==================== LOSSES BREAKDOWN ====================
  // Calculate system losses to reconcile fuel input vs delivered heat
  
  // Cylinder standing losses (if applicable)
  const cylinderStandingKwh = cylinderLoss.kwhPerDay * 365;
  
  // Distribution losses (from boiler type modifier)
  let distributionKwh = 0;
  if (isRegular) {
    // Regular boiler has 3% distribution loss
    distributionKwh = totalHeatKwh * 0.03;
  }
  
  // Cycling and control behavior losses (from DHW efficiency penalty)
  // Only applied for gas boilers because:
  // - Heat pumps have constant efficiency (SCOP) regardless of cycling
  // - Electric resistance heating is 100% efficient at all times
  let cyclingAndControlsKwh = 0;
  if (fuelType === 'gas') {
    // Calculate losses from DHW inefficiencies (difference between ideal and actual)
    const idealDhwFuel = dhw / baseEfficiency;
    const actualDhwFuel = dhw / dhwEfficiency;
    cyclingAndControlsKwh = actualDhwFuel - idealDhwFuel;
  }
  
  // Total losses
  const totalLossesKwh = Math.round(
    (cylinderStandingKwh || 0) + 
    (distributionKwh || 0) + 
    (cyclingAndControlsKwh || 0)
  );
  
  // Build losses breakdown
  const lossesBreakdown: Year1Impact['lossesBreakdown'] = {};
  if (cyclingAndControlsKwh > 0) {
    lossesBreakdown.cyclingAndControlsKwh = Math.round(cyclingAndControlsKwh);
  }
  if (distributionKwh > 0) {
    lossesBreakdown.distributionKwh = Math.round(distributionKwh);
  }
  if (cylinderStandingKwh > 0) {
    lossesBreakdown.cylinderStandingKwh = Math.round(cylinderStandingKwh);
  }
  
  // Calculate fuel input (should match fuelKwh from efficiency calculation)
  // Note: There may be small differences due to rounding and the different calculation methods:
  // - fuelKwh is calculated from efficiency-based division (heat / efficiency)
  // - fuelInputKwh is calculated from losses addition (heat + losses)
  // The losses-based approach makes the breakdown transparent to users
  const fuelInputKwh = Math.round(totalHeatKwh + totalLossesKwh);

  return {
    spaceHeatKwh: spaceHeat,
    dhwKwh: dhw,
    totalHeatKwh: Math.round(totalHeatKwh),
    lossesBreakdown: Object.keys(lossesBreakdown).length > 0 ? lossesBreakdown : undefined,
    totalLossesKwh: totalLossesKwh > 0 ? totalLossesKwh : undefined,
    fuelInputKwh: fuelInputKwh > 0 ? fuelInputKwh : undefined,
    fuelKwh: fuelBreakdown,
    costGbp: Math.round(costGbp),
    carbonKg: Math.round(carbonKg),
    performance: scenario.performance,
  };
}

// ==================== GENERATE YEAR-1 COMPARISON ====================

/**
 * Generate Year-1 comparison from system recommendation output
 *
 * @param output - System recommendation output
 * @returns Year1Comparison object with baseline and options
 */
export function generateYear1Comparison(output: SystemRecOutput): Year1Comparison | null {
  const baseline = output.baselineResult as BaselineResult | undefined;
  const inputEcho = (output as SystemRecOutput).inputEcho as Partial<SystemRecOutput['inputEcho']> | undefined;
  const baselineSmartControl = output.baselineSmartControl ?? false;

  if (!baseline) {
    return null; // Cannot generate without baseline
  }

  // ==================== BASELINE ====================

  // Get heat demand - prioritize LCOO if available, otherwise estimate from inputs
  let annualHeatDemand = 12000; // Fallback only if both LCOO and inputs fail

  const primaryLcoo = output.primary?.lcoo;
  if (primaryLcoo && Number.isFinite(primaryLcoo.annualUsefulHeatKwh) && primaryLcoo.annualUsefulHeatKwh > 0) {
    annualHeatDemand = primaryLcoo.annualUsefulHeatKwh;
  } else if (output.inputEcho) {
    // Estimate demand from property characteristics
    // inputEcho may be undefined or partial, so provide safe fallback
    const input = (output.inputEcho || {}) as Partial<SystemRecInput>;
    const demandEstimate = estimateDemand(input as SystemRecInput);
    annualHeatDemand = demandEstimate.delivered.totalHeatKwh;
    
    // Add demand estimate info to assumptions if it's not using actual usage
    if (!demandEstimate.usedActual && output.assumptions) {
      output.assumptions.push(`Estimated annual heat demand: ${annualHeatDemand.toLocaleString()} kWh/yr (confidence: ${demandEstimate.confidence})`);
      demandEstimate.reasons.forEach(reason => {
        output.assumptions.push(`  - ${reason}`);
      });
    }
  }

  // Get baseline system configuration from input (if available)
  const currentBoiler = (inputEcho as any)?.currentBoiler || 'combi'; // Default to combi if unknown
  const currentWater = (inputEcho as any)?.currentWater || 'on_demand'; // Default to on_demand if unknown
  const currentFuelType = (inputEcho as any)?.currentFuelType || 'gas';
  const baselineFuelType: 'gas' | 'electricity' = currentFuelType === 'electric' ? 'electricity' : 'gas';
  const baselineFuelCost = getTariffPPerKwh(baselineFuelType);

  // Estimate baseline overhead losses based on current system
  // Combis and storage combis have overhead losses from cycling
  let baselineOverheadKwh = 0;
  if (currentBoiler === 'combi' || currentWater === 'on_demand') {
    // Combi: startup + post-purge losses per draw
    // Assume typical 22 draws/day, 0.5 kWh overhead per day
    baselineOverheadKwh = 0.5 * 365; // ~183 kWh/year
  } else if (currentBoiler === 'storage_combi') {
    // Storage combi: lower overhead than regular combi
    baselineOverheadKwh = 0.3 * 365; // ~110 kWh/year
  }

  const baselineTotalHeatKwh = annualHeatDemand + baselineOverheadKwh;

  // Build baseline scenario
  // Use sludge-adjusted efficiency if available for accurate carbon/cost calculations
  let baselineEfficiency = baseline.sludgeAdjustedEfficiency || baseline.initialEfficiency || 0.75;
  
  // Apply smart control uplift to baseline if present
  if (baselineSmartControl && baselineFuelType === 'gas') {
    // Baseline has smart control - apply uplift
    // Use conservative smart_on_off uplift (10%) since we don't know the exact type
    const upliftFactor = 1 / (1 - 0.10); // 10% fuel savings
    baselineEfficiency = Math.min(1.0, baselineEfficiency * upliftFactor);
  }
  
  const baselineScenario: ScenarioData = {
    annualHeatDemand_kWh: baselineTotalHeatKwh,
    systemEfficiency: baselineEfficiency,
    fuelType: baselineFuelType,
    fuelCostPPerKwh: baselineFuelCost,
    performance: getBaselinePerformance(baseline),
    boilerType: currentBoiler,
    cylinderType: currentWater,
  };

  const baselineImpact = buildYear1Impact(baselineScenario);

  // ==================== OPTIONS ====================

  const options: Year1Comparison['options'] = [];
  const allOptions = [output.primary, ...(output.alternatives || [])];
  
  // Extract input from output for SPF range estimation
  const input = output.inputEcho as SystemRecInput | undefined;
  
  // Extract heat loss if available
  const heatLossKw = input?.heatLossKw || input?.knownHeatLossKw;

  for (const option of allOptions) {
    const fuelType = getFuelTypeForSystem(option.boiler);

    // Get performance metadata (includes SPF range for heat pumps)
    const performance = getOptionPerformance(option, input, heatLossKw);
    let efficiency = performance.value;

    const fuelCostPPerKwh = getTariffPPerKwh(fuelType);

    // Apply smart control uplift if this option includes smart control
    // and baseline doesn't have it (to avoid double-counting)
    if (option.includesSmartControl && !baselineSmartControl && fuelType === 'gas') {
      // Determine smart control type from option
      const smartControlType = inputEcho?.thermostatPreference?.preferredControlType;
      const upliftPct = smartControlType === 'smart_load_comp' ? 18 : 10;
      const upliftFactor = 1 / (1 - upliftPct / 100);
      efficiency = Math.min(1.0, efficiency * upliftFactor);
    }

    // Add overhead losses to heat demand (combis have startup/purge losses)
    const annualOverheadKwh = (option.dailyOverheadKWh || 0) * 365;
    const totalAnnualHeatKwh = annualHeatDemand + annualOverheadKwh;

    const optionScenario: ScenarioData = {
      annualHeatDemand_kWh: totalAnnualHeatKwh,
      systemEfficiency: efficiency,
      fuelType,
      fuelCostPPerKwh,
      performance,
      boilerType: option.boiler,
      cylinderType: option.water,
    };

    const optionImpact = buildYear1Impact(optionScenario);

    options.push({
      optionId: option.id,
      label: option.title,
      impact: optionImpact,
    });
  }

  return {
    baseline: baselineImpact,
    options,
  };
}
